import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { getTopicById } from '@/mocks/topics';
import { savedSubjects } from '@/mocks/subjects';
import { savedLessons } from '@/mocks/lessons';
import { useTheme } from '@/contexts/ThemeContext';
import { recordTopicOpen } from '@/mocks/historyStore';
import Pressable from '@/components/base/Pressable';

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  return `${m} min`;
}

export default function TopicPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [activeTab, setActiveTab] = useState<'lessons' | 'quizzes' | 'notes'>('lessons');

  const topic = id ? getTopicById(id) : undefined;
  const subject = topic ? savedSubjects.find((s) => s.id === topic.subjectId) : undefined;

  useEffect(() => {
    if (topic && subject) {
      recordTopicOpen({
        id: topic.id,
        name: topic.name,
        subject: subject.name,
        grade: topic.grade,
      });
    }
  }, [topic?.id, subject?.name]);

  const topicLessons = topic
    ? savedLessons.filter((l) =>
        l.subject.toLowerCase().includes(subject?.name.toLowerCase() || '') ||
        l.title.toLowerCase().includes(topic.name.toLowerCase())
      )
    : [];

  const displayLessons = topicLessons.length > 0 ? topicLessons : savedLessons.slice(0, 3);

  if (!topic || !subject) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-full px-6 text-center ${isDark ? 'bg-[#0F0F10]' : 'bg-white'}`}>
        <div className={`w-16 h-16 rounded-full ${isDark ? 'bg-white/[0.05]' : 'bg-neutral-100'} flex items-center justify-center mb-5`}>
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`ri-error-warning-line ${isDark ? 'text-white/30' : 'text-neutral-300'} text-xl`} />
          </div>
        </div>
        <h2 className={`${isDark ? 'text-white' : 'text-neutral-900'} text-base font-semibold mb-2`}>Topic Not Found</h2>
        <Pressable onClick={() => navigate(-1)} className={`px-6 py-3 ${isDark ? 'bg-white text-black' : 'bg-neutral-900 text-white'} rounded-xl text-xs font-semibold`} scale={0.95}>
          Go Back
        </Pressable>
      </div>
    );
  }

  const bg = isDark ? 'bg-[#0F0F10]' : 'bg-white';
  const textPrimary = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-[#A1A1A6]' : 'text-neutral-400';
  const textMuted = isDark ? 'text-[#8E8E93]' : 'text-neutral-500';
  const cardBg = isDark ? 'bg-[#1C1D21]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/[0.05]' : 'border-neutral-100/80';
  const cardShadow = isDark ? 'shadow-none' : 'shadow-[0_2px_8px_rgba(0,0,0,0.04)]';
  const iconBg = isDark ? 'bg-white/[0.04]' : 'bg-neutral-100';

  const tabs = [
    { id: 'lessons' as const, label: 'Lessons', icon: 'ri-video-line' },
    { id: 'quizzes' as const, label: 'Quizzes', icon: 'ri-questionnaire-line' },
    { id: 'notes' as const, label: 'Notes', icon: 'ri-file-list-line' },
  ];

  return (
    <div className={`flex flex-col min-h-0 ${bg} transition-colors duration-500`}>
      {/* Sticky Header */}
      <div
        className="flex-shrink-0 px-5 pt-6 pb-3 flex items-center gap-3 sticky top-0 z-30 backdrop-blur-xl"
        style={{ backgroundColor: isDark ? 'rgba(15,15,16,0.8)' : 'rgba(255,255,255,0.8)' }}
      >
        <Pressable
          onClick={() => navigate(-1)}
          className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center transition-colors duration-500`}
          scale={0.85}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-arrow-left-line text-sm ${textMuted}`} />
          </div>
        </Pressable>
        <div className="flex-1 min-w-0">
          <h1 className={`text-sm font-semibold ${textPrimary} truncate`}>{topic.name}</h1>
          <p className={`text-[11px] ${textSecondary}`}>{subject.name} &middot; Grade {topic.grade}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Topic Info */}
        <div className="px-5 pt-2 pb-5">
          <div className={`${cardBg} border ${cardBorder} ${cardShadow} rounded-2xl p-5`}>
            <div className="flex items-start gap-3.5">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${subject.color}15` }}
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${topic.icon} text-xl`} style={{ color: subject.color }} />
                </div>
              </div>
              <div className="flex-1">
                <h2 className={`text-base font-bold ${textPrimary}`}>{topic.name}</h2>
                <p className={`text-xs ${textSecondary} mt-0.5 leading-relaxed`}>
                  {topic.description}
                </p>
                <div className="flex items-center gap-3 mt-2.5">
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      topic.difficulty === 'Beginner'
                        ? isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                        : topic.difficulty === 'Intermediate'
                        ? isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'
                        : isDark ? 'bg-rose-500/10 text-rose-400' : 'bg-rose-50 text-rose-600'
                    }`}
                  >
                    {topic.difficulty}
                  </span>
                  <span className={`text-[10px] ${textMuted}`}>{topic.lessonCount} lessons</span>
                  <span className={`text-[10px] ${textMuted}`}>{topic.estimatedHours}h total</span>
                </div>
                {topic.completionPercent > 0 && (
                  <div className="flex items-center gap-2 mt-2.5">
                    <div className={`flex-1 h-1.5 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'} overflow-hidden`}>
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${topic.completionPercent}%`, backgroundColor: subject.color }}
                      />
                    </div>
                    <span className={`text-[10px] font-medium ${textMuted}`}>{topic.completionPercent}%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── AI Lesson Generator Card ── */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, ease: [0.32, 0.72, 0, 1], delay: 0.08 }}
          className="px-5 pb-4"
        >
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: isDark
                ? `linear-gradient(135deg, ${subject.color}20 0%, ${subject.color}08 100%)`
                : `linear-gradient(135deg, ${subject.color}12 0%, ${subject.color}05 100%)`,
              border: `1.5px solid ${subject.color}30`,
            }}
          >
            {/* Header row */}
            <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <i className="ri-sparkling-2-line text-[11px]" style={{ color: subject.color }} />
                  <span
                    className="text-[10px] font-extrabold uppercase tracking-widest"
                    style={{ color: subject.color }}
                  >
                    AI Lesson Generator
                  </span>
                </div>
                <p className={`text-[11px] leading-relaxed ${textSecondary}`}>
                  Get a personalized lesson on{' '}
                  <span className="font-semibold" style={{ color: subject.color }}>
                    {topic.name}
                  </span>{' '}
                  in seconds
                </p>
              </div>
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${subject.color}20` }}
              >
                <i className="ri-robot-2-line text-base" style={{ color: subject.color }} />
              </div>
            </div>

            {/* Action buttons */}
            <div className="px-3 pb-3 grid grid-cols-2 gap-2">
              {/* Scan homework */}
              <Pressable
                onClick={() => {
                  navigate('/scan/start', {
                    state: {
                      subject: subject.name,
                      subjectId: subject.id,
                      subjectColor: subject.color,
                      grade: topic.grade,
                      topic: topic.name,
                      topicId: topic.id,
                      fromTopic: true,
                    },
                  });
                }}
                className="flex flex-col items-center gap-2 rounded-xl py-3.5 px-2 transition-opacity duration-200"
                style={{
                  backgroundColor: isDark ? `${subject.color}15` : `${subject.color}10`,
                  border: `1.5px solid ${subject.color}25`,
                }}
                scale={0.94}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: subject.color }}
                >
                  <i className="ri-camera-line text-white text-base" />
                </div>
                <div className="text-center">
                  <p className={`text-[11px] font-bold ${textPrimary}`}>Scan Homework</p>
                  <p className={`text-[10px] ${textMuted} mt-0.5`}>Upload a photo</p>
                </div>
              </Pressable>

              {/* Generate from topic */}
              <Pressable
                onClick={() => {
                  sessionStorage.setItem('scan_context', JSON.stringify({
                      subject: subject.name,
                      subjectId: subject.id,
                      subjectColor: subject.color,
                      grade: topic.grade,
                      topic: topic.name,
                      topicId: topic.id,
                      fromTopic: true,
                      autoGenerate: true,
                  }));
                  sessionStorage.removeItem('scan_input_image');
                  sessionStorage.setItem('scan_output_mode', 'video');
                  navigate('/scan/analyzing');
                }}
                className="flex flex-col items-center gap-2 rounded-xl py-3.5 px-2 transition-opacity duration-200"
                style={{
                  backgroundColor: isDark ? `${subject.color}15` : `${subject.color}10`,
                  border: `1.5px solid ${subject.color}25`,
                }}
                scale={0.94}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: subject.color }}
                >
                  <i className="ri-sparkling-line text-white text-base" />
                </div>
                <div className="text-center">
                  <p className={`text-[11px] font-bold ${textPrimary}`}>Generate Lesson</p>
                  <p className={`text-[10px] ${textMuted} mt-0.5`}>From this topic</p>
                </div>
              </Pressable>
            </div>

            {/* Context pill */}
            <div className="px-3 pb-3">
              <div
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl"
                style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
              >
                <i className="ri-graduation-cap-line text-[10px]" style={{ color: subject.color }} />
                <span className={`text-[10px] font-medium ${textMuted}`}>
                  {subject.name} &middot; Grade {topic.grade} &middot; {topic.difficulty}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="px-5 pb-4">
          <div className={`flex items-center gap-1 p-1 rounded-xl ${isDark ? 'bg-white/[0.04]' : 'bg-neutral-100'}`}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                    isActive
                      ? isDark ? 'text-black' : 'text-white'
                      : isDark ? 'text-[#6E6E73]' : 'text-neutral-400'
                  }`}
                >
                  {isActive && (
                    <div
                      className={`absolute inset-0 rounded-lg ${isDark ? 'bg-[#F5F5F7]' : 'bg-neutral-900'}`}
                    />
                  )}
                  <div className="w-4 h-4 flex items-center justify-center relative z-10">
                    <i className={`${tab.icon} text-sm`} />
                  </div>
                  <span className="relative z-10">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-5 pb-8">
          {activeTab === 'lessons' && (
            <div className="space-y-2.5">
              {displayLessons.map((lesson) => (
                <Pressable
                  key={lesson.id}
                  onClick={() => navigate(`/lesson/${lesson.id}`, { state: { topicId: topic.id, topicName: topic.name, grade: topic.grade, subjectId: subject.id } })}
                  className={`flex items-center gap-3.5 ${cardBg} border ${cardBorder} ${cardShadow} rounded-xl p-3 w-full transition-colors duration-500`}
                  scale={0.97}
                >
                  <div className="w-20 h-14 rounded-lg flex-shrink-0 overflow-hidden bg-neutral-100">
                    <img
                      src={lesson.videoThumbnail}
                      alt={lesson.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`text-sm font-semibold ${textPrimary} truncate`}>
                      {lesson.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] ${textMuted}`}>{formatDuration(lesson.duration)}</span>
                      <span className={`w-0.5 h-0.5 rounded-full ${isDark ? 'bg-[#48484F]' : 'bg-neutral-300'}`} />
                      <span className={`text-[10px] ${textMuted}`}>{lesson.topicDifficulty}</span>
                    </div>
                    {lesson.completionPercent > 0 && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className={`flex-1 h-1 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'} overflow-hidden`}>
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${lesson.completionPercent}%`, backgroundColor: subject.color }}
                          />
                        </div>
                        <span className={`text-[10px] font-medium ${textMuted}`}>{lesson.completionPercent}%</span>
                      </div>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${subject.color}15` }}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-fill text-sm" style={{ color: subject.color }} />
                    </div>
                  </div>
                </Pressable>
              ))}
            </div>
          )}

          {activeTab === 'quizzes' && (
            <div className="space-y-2.5">
              {displayLessons.slice(0, 2).map((lesson, i) => (
                <Pressable
                  key={`quiz-${i}`}
                  className={`flex items-center gap-3.5 ${cardBg} border ${cardBorder} ${cardShadow} rounded-xl p-4 w-full transition-colors duration-500`}
                  scale={0.97}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${subject.color}15` }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-questionnaire-line text-lg" style={{ color: subject.color }} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`text-sm font-semibold ${textPrimary}`}>
                      {lesson.title} — Quiz
                    </h4>
                    <p className={`text-[11px] ${textSecondary} mt-0.5`}>
                      {lesson.quiz?.length || 5} questions &middot; {lesson.topicDifficulty}
                    </p>
                  </div>
                  <div className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shrink-0" style={{ backgroundColor: subject.color }}>
                    Start
                  </div>
                </Pressable>
              ))}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-2.5">
              {displayLessons.slice(0, 2).map((lesson, i) => (
                <Pressable
                  key={`note-${i}`}
                  className={`flex items-center gap-3.5 ${cardBg} border ${cardBorder} ${cardShadow} rounded-xl p-4 w-full transition-colors duration-500`}
                  scale={0.97}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${subject.color}15` }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-list-line text-lg" style={{ color: subject.color }} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`text-sm font-semibold ${textPrimary}`}>
                      {lesson.title} — Notes
                    </h4>
                    <p className={`text-[11px] ${textSecondary} mt-0.5`}>
                      {lesson.summary.sections.length} sections &middot; {lesson.keyConcepts.length} key concepts
                    </p>
                  </div>
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className={`ri-arrow-right-s-line text-lg ${textMuted}`} />
                  </div>
                </Pressable>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}